import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ScrollView, TouchableOpacity, View, Text } from 'react-native'
import { useNavigate } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import { getDatePickerLocale } from '@/utils/datepickerLocale'
import 'react-datepicker/dist/react-datepicker.css'

import {
  AppBackground,
  BottomActionContainer,
  Button,
  Drop,
  Input,
  ResponsiveLogo
} from '@ui/components'

import {
  InputContainer,
  InputWrapper,
  Headline2,
  Headline5,
  BodyText,
  ErrorText,
  colors,
  DatePickerWrapper
} from '@/aena/styles' // Importando los estilos del PetAdd

import { ReactComponent as ChevronDownIcon } from '@/assets/icons/aena/aena_chevron_down.svg'
import { usePetInsurance } from '@front/logic'
import { addExternalInsuranceAPI, InsuranceCompany } from '@front/apis'
import { useAppSelector, setLoading, useAppDispatch } from '@front/store'
import Toast from 'react-native-toast-message'
import { t } from 'i18next'
import { PETPASS_ExternalInsuranceType, PETPASS_insurance_recurrence } from '@/db/schema/types'
import { HeaderWithBackAena } from '@/components/headerwithback'
import CheckBoxCustom from '@/components/customcheckbox'


interface ExternalInsuranceForm {
  photoPdf: File | null
  insuranceCompany: string
  policyNumber: string
  insuranceType?: PETPASS_ExternalInsuranceType
  paymentRecurrence?: PETPASS_insurance_recurrence
  expirationDate: string
  acceptsConditions: boolean
}


export const ExternalInsuranceAdd: FC = () => {
  const PAYMENT_RECURRENCE = [
    { id: PETPASS_insurance_recurrence.MONTHLY, title: t('insurance.payment.monthly'), data: {} },
    { id: PETPASS_insurance_recurrence.QUARTERLY, title: t('insurance.payment.quarterly'), data: {} },
    { id: PETPASS_insurance_recurrence.ANNUAL, title: t('insurance.payment.annual'), data: {} },
  ]
  const INSURANCE_TYPES = [
    { id: PETPASS_ExternalInsuranceType.CIVIL_LIABILITY, title: t('insurance.types.liability'), data: {} },
    { id: PETPASS_ExternalInsuranceType.VETERINARY_LIABILITY, title: t('insurance.types.veterinary_liability'), data: {} },
    { id: PETPASS_ExternalInsuranceType.HOME_LIABILITY, title: t('insurance.types.home_liability'), data: {} },
    { id: PETPASS_ExternalInsuranceType.OTHER, title: t('insurance.types.other'), data: {} },
  ]
    
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const { getInsuranceCompanies } = usePetInsurance()
  const dispatch = useAppDispatch()
  const { guid } = useAppSelector((state) => state.selectedPet)

  const [formData, setFormData] = useState<ExternalInsuranceForm>({
    photoPdf: null,
    insuranceCompany: '',
    policyNumber: '',
    insuranceType: undefined,
    paymentRecurrence: undefined,
    expirationDate: '',
    acceptsConditions: true
  })

  const [expirationDate, setExpirationDate] = useState<Date | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})
  const [selectedFileName, setSelectedFileName] = useState<string>('')
  // Feedback de campos obligatorios: marca cuándo el usuario ha intentado enviar
  // y si ya ha empezado a interactuar con el formulario.
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false)
  const [hasInteracted, setHasInteracted] = useState<boolean>(false)

  


  const [companies, setCompanies] = useState<InsuranceCompany[]>([])

  const fetchCompanies = async () => {
    const res = await getInsuranceCompanies()
    setCompanies(res)
  }

  useEffect(() => {
    fetchCompanies()
  }, [])


  const dropCompanies = useMemo(() => {
    return companies.sort((a, b) => a.order - b.order).map((c) => ({
      id: c.guid,
      title: c.name,
      data: {},
    }))
  }, [companies])


  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasInteracted(true)
    const file = event.target.files?.[0]
    if (file && file.type === 'application/pdf') {
      setFormData(prev => ({ ...prev, photoPdf: file }))
      setSelectedFileName(file.name)
    } else if (file) {
      alert(t('insurance.errors.pdf_only'))
    }
  }

  const handleInputChange = (field: keyof ExternalInsuranceForm, value: any) => {
    setHasInteracted(true)
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const canSubmit = useMemo(() => {
    return formData.photoPdf && formData.insuranceCompany && formData.policyNumber && formData.insuranceType && formData.paymentRecurrence && formData.expirationDate && formData.acceptsConditions
  }, [formData])

  // Lista de campos obligatorios que faltan, con su clave (para marcar el error
  // inline) y su etiqueta i18n (para el resumen visible junto al botón).
  const missingFields = useMemo(() => {
    const missing: { key: string; label: string }[] = []
    if (!formData.photoPdf) missing.push({ key: 'photoPdf', label: t('insurance.external.policy_pdf_label') })
    if (!formData.insuranceCompany) missing.push({ key: 'insuranceCompany', label: t('insurance.external.company_label') })
    if (!formData.insuranceType) missing.push({ key: 'insuranceType', label: t('insurance.external.type_label') })
    if (!formData.paymentRecurrence) missing.push({ key: 'paymentRecurrence', label: t('insurance.external.payment_recurrence_label') })
    if (!formData.policyNumber?.trim()) missing.push({ key: 'policyNumber', label: t('insurance.external.policy_number_label') })
    if (!expirationDate) missing.push({ key: 'expirationDate', label: t('insurance.external.expiration_date_label') })
    if (!formData.acceptsConditions) missing.push({ key: 'acceptsConditions', label: t('insurance.external.accept_conditions_prefix') })
    return missing
  }, [formData, expirationDate])

  // Errores que deben mostrarse inline en cada campo obligatorio vacío.
  // Se muestran tras un intento de envío o una vez el usuario ha empezado a rellenar.
  const requiredErrorByField = (field: string): string => {
    if (fieldErrors[field]) return fieldErrors[field]
    if (!submitAttempted && !hasInteracted) return ''
    const missing = missingFields.find((m) => m.key === field)
    if (!missing) return ''
    const errorKeys: { [key: string]: string } = {
      photoPdf: 'insurance.errors.pdf_required',
      insuranceCompany: 'insurance.errors.company_required',
      policyNumber: 'insurance.errors.policy_number_required',
      insuranceType: 'insurance.errors.insurance_type_required',
      paymentRecurrence: 'insurance.errors.payment_recurrence_required',
      expirationDate: 'insurance.errors.expiration_date_required',
      acceptsConditions: 'insurance.errors.accept_conditions_required',
    }
    return t(errorKeys[field])
  }

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    if (!formData.photoPdf) {
      errors.photoPdf = t('insurance.errors.pdf_required')
    }

    if (!formData.insuranceCompany) {
      errors.insuranceCompany = t('insurance.errors.company_required')
    }

    if (!formData.policyNumber?.trim()) {
      errors.policyNumber = t('insurance.errors.policy_number_required')
    }

    if (!formData.insuranceType) {
      errors.insuranceType = t('insurance.errors.insurance_type_required')
    }

    if (!formData.paymentRecurrence) {
      errors.paymentRecurrence = t('insurance.errors.payment_recurrence_required')
    }

    if (!expirationDate) {
      errors.expirationDate = t('insurance.errors.expiration_date_required')
    }

    if (!formData.acceptsConditions) {
      errors.acceptsConditions = t('insurance.errors.accept_conditions_required')
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    // Marca el intento de envío para que se muestre el error inline de cada campo
    // obligatorio vacío y el resumen de faltantes.
    setSubmitAttempted(true)
    if (!validateForm()) {
      return
    }

    dispatch(setLoading(true))
    try {
      console.log('formData----', { formData })
      if (!formData.insuranceType) {
        throw new Error('Insurance type is required')
      }
      if (!formData.paymentRecurrence) {
        throw new Error('Payment recurrence is required')
      }

      const res = await addExternalInsuranceAPI({
        guid,
        payload: {
          file: formData.photoPdf,
          type: formData.insuranceType,
          policyNumber: formData.policyNumber,
          expirationDate: formData.expirationDate,
          // coverage: formData.coverageAmount,
          //terms://
          // price: formData.receiptAmount,
          companyGUID: formData.insuranceCompany,
          recurrence: formData.paymentRecurrence,
          confirm_veracity: formData.acceptsConditions

        },
      })

      Toast.show({
        type: 'success',
        text1: t('insurance.success.added_title'),
        text2: res.message,
      })

      navigate(-1)

    } catch (error: any) {
      console.error('Error al guardar seguro externo:', error)
      Toast.show({
        type: 'error',
        text1: t('insurance.errors.add_error_title'),
        text2: error.message,
      })
    } finally {
      dispatch(setLoading(false))
    }
  }



  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena title={t('insurance.external.add_title')} />

        <View style={{ marginBottom: 1 }}>
          <Headline2 style={{ padding: 15, color: colors.azulCorporativo, borderRadius: 10 }}>
            {t('insurance.external.information_title')}
          </Headline2>
        </View>

        <BottomActionContainer>
          {/* Fragment: BottomActionContainer.children es ReactElement(s); el Fragment
              recibe un único hijo y dentro acepta comentarios JSX y condicionales. */}
          <>
            {/* Foto PDF */}
            <InputWrapper>
              <Headline5>{t('insurance.external.policy_pdf_label')} *</Headline5>
              <InputContainer>
                <TouchableOpacity
                  onPress={() => fileInputRef.current?.click()}
                  style={{
                    alignItems: 'center',
                    padding: 10,
                    borderWidth: 2,
                    borderColor: '#ccc',
                    borderRadius: 8,
                    backgroundColor: '#f8f8f8',
                    borderStyle: 'dashed',
                  }}
                >
                  <BodyText>
                    {selectedFileName || t('insurance.external.select_pdf_file')}
                  </BodyText>
                </TouchableOpacity>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                {!!requiredErrorByField('photoPdf') && <ErrorText>{requiredErrorByField('photoPdf')}</ErrorText>}
              </InputContainer>
            </InputWrapper>

            {/* Compañía Aseguradora */}
            <InputWrapper>
              <Headline5>{t('insurance.external.company_label')} *</Headline5>
              <InputContainer>
                <Drop
                  buttonText={t('common.continue')}
                  data={dropCompanies}
                  active={dropCompanies.findIndex(c => c.id === formData.insuranceCompany)}
                  selectedItem={dropCompanies.findIndex(c => c.id === formData.insuranceCompany)}
                  onPress={(item) => handleInputChange('insuranceCompany', item.id)}
                  placeholder={t('insurance.external.select_company')}
                  icon={<ChevronDownIcon width={22} height={22} />}
                  activeColor="#96ce00"
                  color="aena"

                  variant="outlined"
                />
                {!!requiredErrorByField('insuranceCompany') && <ErrorText>{requiredErrorByField('insuranceCompany')}</ErrorText>}
              </InputContainer>
            </InputWrapper>

            {/* Tipo de Seguro y Recurrencia de Pago */}
            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
                <Headline5>{t('insurance.external.type_label') + ' *'}</Headline5>
                <InputContainer>
                  <Drop
                    buttonText={t('common.continue')}
                    data={INSURANCE_TYPES}
                    active={INSURANCE_TYPES.findIndex(c => c.id === formData.insuranceType)}
                    selectedItem={INSURANCE_TYPES.findIndex(c => c.id === formData.insuranceType)}
                    onPress={(item) => handleInputChange('insuranceType', item.id)}
                    placeholder={t('insurance.external.select_type')}
                    icon={<ChevronDownIcon width={22} height={22} />}
                    activeColor="#96ce00"
                    variant="outlined"
                    color="aena"
                  />
                  {!!requiredErrorByField('insuranceType') && <ErrorText>{requiredErrorByField('insuranceType')}</ErrorText>}
                </InputContainer>
              </View>
              <View style={{ flex: 1 }}>
                <Headline5>{t('insurance.external.payment_recurrence_label') + ' *'}</Headline5>
                <InputContainer>
                  <Drop
                    buttonText={t('common.continue')}
                    data={PAYMENT_RECURRENCE}
                    active={PAYMENT_RECURRENCE.findIndex(c => c.id === formData.paymentRecurrence)}
                    selectedItem={PAYMENT_RECURRENCE.findIndex(c => c.id === formData.paymentRecurrence)}
                    onPress={(item) => handleInputChange('paymentRecurrence', item.id)}
                    placeholder={t('insurance.external.select_recurrence')}
                    icon={<ChevronDownIcon width={22} height={22} />}
                    activeColor="#96ce00"
                    variant="outlined"
                    color="aena"
                  />
                  {!!requiredErrorByField('paymentRecurrence') && <ErrorText>{requiredErrorByField('paymentRecurrence')}</ErrorText>}
                </InputContainer>
              </View>
              
            </View>

            {/* Número de Póliza */}
            <InputWrapper>
                <Headline5>{t('insurance.external.policy_number_label') + ' *'}</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: formData.policyNumber,
                      onChangeText: (text: string) => handleInputChange('policyNumber', text),
                      placeholder: t('insurance.external.policy_number_placeholder'),
                    }}
                  />
                  {!!requiredErrorByField('policyNumber') && <ErrorText>{requiredErrorByField('policyNumber')}</ErrorText>}
                </InputContainer>
              </InputWrapper>

            {/* Fecha de Expiración */}
            <InputWrapper>
              <Headline5>{t('insurance.external.expiration_date_label') + ' *'}</Headline5>
              <InputContainer>
                <DatePickerWrapper>
                  <DatePicker
                    selected={expirationDate}
                    onChange={(date: Date | null) => {
                      setHasInteracted(true)
                      setExpirationDate(date)
                      handleInputChange('expirationDate', date?.toISOString() ?? '')
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t('insurance.external.select_date')}
                    className="datepicker-styled"
                    popperPlacement="bottom-start"
                    dropdownMode="select"
                    locale={getDatePickerLocale()}
                    minDate={new Date()}
                    showMonthDropdown
                    showYearDropdown
                  />
                </DatePickerWrapper>
                {!!requiredErrorByField('expirationDate') && <ErrorText>{requiredErrorByField('expirationDate')}</ErrorText>}
              </InputContainer>
            </InputWrapper>

            {/* Resumen de campos obligatorios que faltan. Se muestra junto al botón
                tras un intento de envío o una vez el usuario ha empezado a rellenar. */}
            {(submitAttempted || hasInteracted) && missingFields.length > 0 && (
              <View
                style={{
                  backgroundColor: '#fdecea',
                  borderColor: '#e74c3c',
                  borderWidth: 1,
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <ErrorText style={{ fontWeight: 'bold' }}>
                  {t('insurance.external.information_title')}
                </ErrorText>
                <ErrorText>
                  {`Faltan por completar: ${missingFields.map((m) => m.label).join(', ')}`}
                </ErrorText>
              </View>
            )}

            {/* El botón siempre es pulsable: si el formulario es inválido, handleSubmit
                no envía y muestra el feedback de campos obligatorios. */}
            <Button
              title={t('insurance.external.save_button')}
              size="block"
              textType="cta_medium"
              borderRadius={5}
              buttonWrapperStyle={{ backgroundColor: canSubmit ? '#96ce00' : '#c7c7c7' }}
              color="primary"
              onPress={handleSubmit}
            />
          </>
        </BottomActionContainer>
      </View>
    </AppBackground>
  )
}

export default ExternalInsuranceAdd